export type OutputPathToken = string | number;

export interface OutputPathResolution {
  found: boolean;
  value: any;
}

function parsePathSegment(segment: string): OutputPathToken[] | null {
  if (segment === "") return [];

  const matches = segment.match(/([^[\]]+)|\[(\d+)\]/g);
  if (matches === null || matches.join("") !== segment) {
    return null;
  }

  return matches.map((match) =>
    match.startsWith("[") ? parseInt(match.slice(1, -1), 10) : match
  );
}

function tokenizeOutputPath(path: string): OutputPathToken[] | null {
  return path
    .split("@#")
    .reduce<OutputPathToken[] | null>((tokens, segment) => {
      if (tokens === null) {
        return null;
      }

      const parsed = parsePathSegment(segment);
      if (parsed === null) {
        return null;
      }

      return [...tokens, ...parsed];
    }, []);
}

export function resolveOutputPath(
  source: any,
  path: string
): OutputPathResolution {
  if (path === "") {
    return { found: true, value: source };
  }

  const tokens = tokenizeOutputPath(path);
  if (tokens === null) {
    return { found: false, value: undefined };
  }

  return tokens.reduce<OutputPathResolution>(
    (resolution, token) => {
      if (!resolution.found) {
        return resolution;
      }

      const current = resolution.value;
      if (
        current === null ||
        current === undefined ||
        typeof current !== "object"
      ) {
        return { found: false, value: undefined };
      }

      if (!Object.prototype.hasOwnProperty.call(current, token)) {
        return { found: false, value: undefined };
      }

      const nextValue = current[token as keyof typeof current];
      return nextValue !== undefined
        ? { found: true, value: nextValue }
        : { found: false, value: undefined };
    },
    { found: true, value: source }
  );
}

export function stringifyOutputPathValue(value: any): string {
  if (value === undefined) return "";
  return typeof value === "string" ? value : JSON.stringify(value);
}
